import React, {useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {SharedField} from '@api/shared/components/field'
import {ButtonNew} from '@components/common/base/buttons'
import {InputNew} from '@components/common/base/input-new'
import {Popup} from '@components/common/base/popup'
import {InputWithSuggest} from '@components/common/input-with-suggest/input-with-suggest'
import {crmStore} from '@components/crm/crm-store'
import {PageService} from '@extensions/page-service'

export const NewDealPopup = () => {
  const firstInputRef = useRef<HTMLInputElement>(null)
  const dealTitleWasEditedByUserManually = useRef(false)

  const [contactPersonValue, setContactPersonValue] = useState('')
  const [contactPersonHub, setContactPersonHub] = useState<Hub | undefined>()

  const [organizationValue, setOrganizationValue] = useState('')
  const [organizationHub, setOrganizationHub] = useState<Hub | undefined>()

  const [dealTitleValue, setDealTitleValue] = useState('')
  const [dealAmountValue, setDealAmountValue] = useState('')

  const project = PageService.getCurrentProject()
  const contactPersons = crmStore.getProjectContactPersons(project)
  const organizations = crmStore.getProjectOrganizations(project)

  useEffect(() => {
    setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus()
      }
    }, 50)
  }, [])

  const onSubmit = () => {
    crmStore.createNewDeal({
      title: dealTitleValue,
      contactPerson: contactPersonHub ?? contactPersonValue,
      organization: organizationHub ?? organizationValue,
      dealAmount: dealAmountValue
    })
  }

  const autofillDealTitle = (value: string) => {
    if (!dealTitleWasEditedByUserManually.current) {
      const normalized = value.trim()
      const nextTitle = normalized.length
        ? Lang.msg.crm.dealTitleAutofill(normalized)
        : ''
      setDealTitleValue(nextTitle)
    }
  }

  const canSubmit =
    dealTitleValue.trim().length > 0 &&
    (organizationValue.trim().length > 0 ||
      contactPersonValue.trim().length > 0)

  return (
    <Popup
      width={432}
      autoHeight={true}
      title={Lang.msg.crm.newDealPopupTitle}
      close={crmStore.closeNewDealPopup}
      contentPaddings={24}
      footerPaddings={24}
      footer={
        <div className="flex justify-end">
          <div className="flex gap-8">
            <ButtonNew onClick={crmStore.closeNewDealPopup} color="secondary">
              {Lang.msg.cancel}
            </ButtonNew>
            <ButtonNew disabled={!canSubmit} onClick={onSubmit}>
              {Lang.msg.add}
            </ButtonNew>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-12">
        <SharedField label={Lang.msg.crm.organization}>
          <InputWithSuggest
            inputRef={firstInputRef}
            value={organizationValue}
            getAddAsNewLabel={
              Lang.msg.crm.newDealForm.addAsNewOrganizationLabel
            }
            onChange={value => {
              setOrganizationValue(value)
              setOrganizationHub(undefined)

              // Organization title has higher priority for auto-fill.
              // If the user hasn't manually entered a deal title yet,
              // it's acceptable to override it using the contact's title.
              if (!dealTitleWasEditedByUserManually.current) {
                if (value.trim().length) {
                  autofillDealTitle(value)
                }
                // Restore contact value if organization title was cleared.
                else if (contactPersonValue.length) {
                  autofillDealTitle(contactPersonValue)
                }
              }
            }}
            onSelect={item => {
              setOrganizationHub(
                item.isNew
                  ? undefined
                  : organizations.find(c => c.id === item.id)
              )
            }}
            items={organizations.map(org => ({
              id: org.id,
              title: org.title
            }))}
            iconLeft="IconBriefcase"
            iconRight={organizationHub ? 'IconCheck' : undefined}
            placeholder={Lang.msg.crm.organization}
          />
        </SharedField>

        <SharedField label={Lang.msg.crm.contactPerson}>
          <InputWithSuggest
            value={contactPersonValue}
            getAddAsNewLabel={
              Lang.msg.crm.newDealForm.addAsNewContactPersonLabel
            }
            onChange={value => {
              setContactPersonValue(value)
              setContactPersonHub(undefined)

              // We use contact title for autofill only if company title
              // is missing, as organization title has higher priority.
              if (
                organizationValue.trim().length === 0 &&
                !dealTitleWasEditedByUserManually.current
              ) {
                autofillDealTitle(value)
              }
            }}
            onSelect={item => {
              setContactPersonHub(
                item.isNew
                  ? undefined
                  : contactPersons.find(c => c.id === item.id)
              )
            }}
            items={contactPersons.map(org => ({
              id: org.id,
              title: org.title
            }))}
            iconLeft="IconUserCircle"
            iconRight={contactPersonHub ? 'IconCheck' : undefined}
            placeholder={Lang.msg.crm.contactPerson}
          />
        </SharedField>

        <SharedField label={Lang.msg.crm.dealTitle}>
          <InputNew
            value={dealTitleValue}
            placeholder={Lang.msg.crm.newDealForm.enterTitlePlaceholder}
            onChange={value => {
              setDealTitleValue(value)
              dealTitleWasEditedByUserManually.current = value.trim().length > 0
            }}
          />
        </SharedField>

        <div style={{width: '216px'}}>
          <SharedField label={Lang.msg.crm.dealAmountStickerTitle}>
            <InputNew
              value={dealAmountValue}
              placeholder={Lang.msg.crm.newDealForm.enterAmountPlaceholder}
              onChange={setDealAmountValue}
              iconRight="IconCurrencyRubel"
            />
          </SharedField>
        </div>
      </div>
    </Popup>
  )
}
